/* eslint-disable prettier/prettier */

/**
 * CustomEvent() polyfill
 * https://developer.mozilla.org/en-US/docs/Web/API/CustomEvent/CustomEvent#Polyfill
 */
(function () {
  if (typeof window.CustomEvent === "function") {
    return false;
  }

  function CustomEvent(event, params) {
    params = params || { bubbles: false, cancelable: false, detail: undefined };
    const evt = document.createEvent("CustomEvent");
    evt.initCustomEvent(
      event,
      params.bubbles,
      params.cancelable,
      params.detail
    );
    return evt;
  }

  CustomEvent.prototype = window.Event.prototype;

  window.CustomEvent = CustomEvent;
  return true;
})();

import { GlobalWorkerOptions } from "pdfjs-lib";
import { TWViewerPdfApp } from "./app.js";

const pdfWorkerLocation =
  typeof PDFJSDev === "undefined" || !PDFJSDev.test("PRODUCTION")
    ? "../src/worker_loader.js"
    : "../build/pdf.worker.js";

class ResizeObserver {
  constructor(callback) {
    this.observables = [];
    // Array of observed elements that looks like this:
    // [{
    //   el: domNode,
    //   size: {height: x, width: y}
    // }]
    this.boundCheck = this.check.bind(this);
    this.boundCheck();
    this.callback = callback;
  }

  observe(el) {
    if (this.observables.some(observable => observable.el === el)) {
      return;
    }
    const newObservable = {
      el,
      size: {
        height: el.clientHeight,
        width: el.clientWidth,
      },
    };
    this.observables.push(newObservable);
  }

  unobserve(el) {
    this.observables = this.observables.filter(obj => obj.el !== el);
  }

  disconnect() {
    this.observables = [];
  }

  check() {
    const changedEntries = this.observables
      .filter(obj => {
        const currentHeight = obj.el.clientHeight;
        const currentWidth = obj.el.clientWidth;
        if (
          obj.size.height !== currentHeight ||
          obj.size.width !== currentWidth
        ) {
          obj.size.height = currentHeight;
          obj.size.width = currentWidth;
          return true;
        }
        return false;
      })
      .map(obj => obj.el);
    if (changedEntries.length > 0) {
      this.callback(changedEntries);
    }
    window.requestAnimationFrame(this.boundCheck);
  }
}

GlobalWorkerOptions.workerSrc = pdfWorkerLocation;
window._twViewerPdfApps = [];
const frontEnd = true;
let TWobserver = null;

/**
 * Manager for the PDF Viewers.
 */
window.TWPdfViewerUtil = {};

/**
 * @returns {string[]} List of built-in themes
 */
window.TWPdfViewerUtil.getBuiltInThemes = function () {
  return [
    "blue-circle",
    "blue-element",
    "blue-grey",
    "colorful-morphism",
    "dark-cyan",
    "dynamic-purple",
    "light",
    "purple-blue",
    "red-purple",
    "forest-brown",
    "white-shapes",
  ];
};

window.TWPdfViewerUtil.removeViewer = function (id) {
  let index = -1;
  for (let i = 0; i < window._twViewerPdfApps.length; i++) {
    if (window._twViewerPdfApps[i].id === id) {
      index = i;
      break;
    }
  }

  if (index > -1) {
    window._twViewerPdfApps.splice(index, 1);
  }
};

window.TWPdfViewerUtil.addViewer = function (viewer) {
  window._twViewerPdfApps.push(viewer);
};

/**
 * @returns {TWViewerPdfApp[]} List of active PDF Viewers
 */
window.TWPdfViewerUtil.getActiveViewers = function () {
  return window._twViewerPdfApps;
};

/**
 * Get the viewer for the given id.
 * @param {string} id
 * @returns TWViewerPdfApp
 */
window.TWPdfViewerUtil.getViewer = function (id) {
  //
  for (let i = 0; i < window._twViewerPdfApps.length; i++) {
    if (window._twViewerPdfApps[i].id === id) {
      return window._twViewerPdfApps[i];
    }
  }
  return null;
};

/**
 * Change the theme of the viewer.
 * It will change a theme for all the viewers on the page.
 * @param {string} themeName - Name of the built-in theme.
 * @see TWPdfViewerUtil.getBuiltInThemes
 */
window.TWPdfViewerUtil.changeThemeByName = function (themeName) {
  console.log("TWPdfViewerUtil.changeThemeByName", themeName);
  const link = document.getElementById("tw-web-theme");
  const isStylesheet =
    link && link.tagName === "LINK" && link.rel === "stylesheet";
  if (isStylesheet) {
    if (!frontEnd) {
      link.href = `/_content/Terminalworks.PdfViewer.AspNetCore/css/themes/${themeName}/tw-pdf-viewer-theme.css`;
    } else {
      link.href = `css/themes/${themeName}/tw-pdf-viewer-theme.css`;
    }
  } else {
    console.error(
      "<link> stylesheet element with id='tw-web-theme' was not found."
    );
  }
};

/**
 * Change the theme of the viewer.
 * It will change a theme for all the viewers on the page.
 * @param {string} srcUrl - URL of the theme
 */
window.TWPdfViewerUtil.changeThemeBySrcUrl = function (srcUrl) {
  console.log("TWPdfViewerUtil.changeThemeBySrcUrl", srcUrl);
  const link = document.getElementById("tw-web-theme");
  const isStylesheet =
    link && link.tagName === "LINK" && link.rel === "stylesheet";
  if (isStylesheet) {
    link.href = srcUrl;
  } else {
    console.error(
      "<link> stylesheet element with id='tw-web-theme' was not found."
    );
  }
};

function onLoadDocument(event) {
  const viewersDiv = document.getElementsByClassName(
    "tw2018elem_mainContainer"
  );
  for (let i = 0; i < viewersDiv.length; i++) {
    const viewerDiv = viewersDiv[i];
    if (window.TWPdfViewerUtil.getViewer(viewerDiv.id)) {
      // already initialized, do nothing
      continue;
    }
    const isStatic =
      viewerDiv.dataset.static && viewerDiv.dataset.static === "Yes";
    if (frontEnd || isStatic) {
      if (tryToConnectTWViewer(viewerDiv)) {
        const viewerJS = window.TWPdfViewerUtil.getViewer(viewerDiv.id);

        if (viewerJS.options.documentUrl) {
          window.loadTWViewerDocument(
            viewerDiv.id,
            viewerJS.options.documentUrl,
            viewerJS.options.password
          );
        }
      }
    }
  }
  if (frontEnd) {
    return;
  }
  if (!TWobserver) {
    activateMutationObserver();
  }
  // za sada staviti da je uvjet da ako zeli za static ucitati automatski
  // dokument, tada treba staviti isStatic="Yes" ili omoguciti mu da moze
  // nekako preko JS pozvati loadDocument
  // activateFallBackMechanism();
}

/* function activateFallBackMechanism() {
// fallback mechanism - if not explicity dataset.static='Yes'
// we need to wait for server to call tryToAddTWViewer if Server
// or ServerPrendered.
// Otherwise, after max. 3 seconds it will try to add TWViewer instances
// if
let timeout = 0;
const interval = this.setInterval(function () {
  if (isTWConnected) {
    clearInterval(interval);
    return;
  }
  timeout += 100;
  if (timeout > 3000) {
    if (!isTWConnected) {
      window.tryToAddTWViewer();
    }
    clearInterval(interval);
  }
}, 100);
} */

function activateMutationObserver() {
  const targetNode = document.body;

  // Options for the observer (which mutations to observe)
  const config = {
    attributes: true,
    childList: true,
    subtree: true,
    characterData: true,
  };

  // Create an observer instance linked to the callback function
  TWobserver = new MutationObserver(mutationCallback);

  // Start observing the target node for configured mutations
  TWobserver.observe(targetNode, config);

  // Later, you can stop observing
  // TWobserver.disconnect();
}

window.connectTWViewer = id => {
  const viewerDiv = document.getElementById(id);
  if (!viewerDiv) {
    console.error(`Viewer div with ${id} not found.`);
    return;
  }

  const optionsVal = viewerDiv.dataset.options;
  const options = JSON.parse(optionsVal);
  window.TWPdfViewerUtil.addViewer(new TWViewerPdfApp(options, viewerDiv));
};

// Callback function to execute when mutations are observed
const mutationCallback = function (mutationsList, observer) {
  // Use traditional 'for loops' for IE 11
  for (let i = 0; i < mutationsList.length; i++) {
    const mutation = mutationsList[i];
    if (mutation.type !== "childList") {
      // console.log(mutation.type);
      return;
    }

    if (mutation.removedNodes.length > 0) {
      for (let j = 0; j < mutation.removedNodes.length; j++) {
        const removed = mutation.removedNodes[j];
        if (removed.className === "tw2018elem_mainContainer") {
          console.log(`tw2018elem_mainContainer with id ${removed.id} removed`);
          if (window.twViewerPdfApps && window.twViewerPdfApps[removed.id]) {
            window.twViewerPdfApps[removed.id] = null;
          }
          /* const viewerDiv = document.getElementById(removed.id);
          if (viewerDiv) {
            tryToConnectTWViewer(viewerDiv);
          }
          */
        }
      }
    }

    if (mutation.addedNodes.length > 0) {
      for (let k = 0; k < mutation.addedNodes.length; k++) {
        const added = mutation.addedNodes[k];
        // console.log("added node", added.className, added);
        if (added.className === "tw2018elem_mainContainer") {
          console.log(`tw2018elem_mainContainer with id ${added.id} added`);
          tryToConnectTWViewer(added);
        }
      }
    }
  }
};

function tryToConnectTWViewer(viewerDiv) {
  const height = viewerDiv.getBoundingClientRect().height;
  if (height > 0) {
    window.connectTWViewer(viewerDiv.id);
    return true;
  }
  activateSizeResizeObserver(viewerDiv);
  return false;
}

function activateSizeResizeObserver(div) {
  // dodan je, ali nije jos dobro iscrtan
  new ResizeObserver(function (mutations) {
    if (mutations[0].contentRect.height > 0) {
      this.disconnect();
      window.connectTWViewer(mutations[0].id);
    }
  }).observe(div);
}

// sluzi samo za privremeni test
window.showTWLog = msg => {
  console.log("showTWLog", msg);
  const viewersDiv = document.getElementsByClassName(
    "tw2018elem_mainContainer"
  );
  for (let i = 0; i < viewersDiv.length; i++) {
    const viewerDiv = viewersDiv[i];
    const viewerContainer = viewerDiv.getElementsByClassName(
      "tw2018elem_viewerContainer"
    )[0];
    const height = viewerContainer.getBoundingClientRect().height;
    console.log("viewerContainer height", height);
  }
  // window.returnArrayAsync();
};

window.initMissingTWViewerDocument = id => {
  const viewerJS = window.TWPdfViewerUtil.getViewer(id);
  if (!viewerJS) {
    const viewerDiv = document.getElementById(id);
    if (viewerDiv) {
      tryToConnectTWViewer(viewerDiv);
    }
  }
};

window.loadTWViewerDocument = (id, documentUrl, password) => {
  const viewerJS = window.TWPdfViewerUtil.getViewer(id);
  viewerJS.loadDocumentFromUrl(documentUrl, password);
};

window.addEventListener("load", function (event) {
  console.log("in load");
  onLoadDocument(event);
});

// ponekad se load event dogodi prije nego li dodje
// do tuda pa zato uvijek ovo zovem
// if (frontEnd) {
onLoadDocument();
// }
